import axios from 'axios'
import { PublicClientApplication, type AuthenticationResult, type RedirectRequest } from '@azure/msal-browser'
import { buildUrl } from './authService'

export type AzureAuthUserType = 'EMPLOYEE' | 'CONTRACTOR'

type AzureConfig = {
  tenantId: string
  clientId: string
  redirectUri: string
  authority: string
}

type AzureAuthConfigDto = {
  directoryTenantId?: string | null
  clientId?: string | null
  authority?: string | null
  redirectUri?: string | null
  contractorDirectoryTenantId?: string | null
  contractorClientId?: string | null
  contractorAuthority?: string | null
  contractorRedirectUri?: string | null
}

const interactionKeyPrefix = 'eptw.azure.interaction'
const interactionTtlMs = 5 * 60 * 1000
const msalInstances = new Map<string, PublicClientApplication>()
const azureConfigCache = new Map<AzureAuthUserType, AzureConfig | null>()
const azureConfigRequests = new Map<AzureAuthUserType, Promise<AzureConfig | null>>()

const normalizeUserType = (userType?: string): AzureAuthUserType =>
  userType?.trim().toUpperCase() === 'CONTRACTOR' ? 'CONTRACTOR' : 'EMPLOYEE'

const trimOrEmpty = (value?: string | null) => value?.trim() ?? ''

const mapApiAzureConfig = (config: AzureAuthConfigDto | null | undefined, userType: AzureAuthUserType): AzureConfig | null => {
  if (!config) return null

  if (userType === 'CONTRACTOR') {
    const tenantId = trimOrEmpty(config.contractorDirectoryTenantId)
    const clientId = trimOrEmpty(config.contractorClientId)
    const redirectUri = trimOrEmpty(config.contractorRedirectUri)
    const authority = trimOrEmpty(config.contractorAuthority)

    if (!tenantId || !clientId || !redirectUri || !authority) {
      return null
    }

    return { tenantId, clientId, redirectUri, authority }
  }

  const tenantId = trimOrEmpty(config.directoryTenantId)
  const clientId = trimOrEmpty(config.clientId)
  const redirectUri = trimOrEmpty(config.redirectUri)
  const authority = trimOrEmpty(config.authority)

  if (!tenantId || !clientId || !redirectUri || !authority) {
    return null
  }

  return { tenantId, clientId, redirectUri, authority }
}

const fetchAzureConfigFromApi = async (userType: AzureAuthUserType): Promise<AzureConfig | null> => {
  const response = await axios.get<AzureAuthConfigDto>(buildUrl('/api/v1/azure-auth-configs/active'), {
    params: { userType },
  })

  return mapApiAzureConfig(response.data, userType)
}

export const getAzureConfig = async (userType?: string): Promise<AzureConfig | null> => {
  const normalized = normalizeUserType(userType)
  if (azureConfigCache.has(normalized)) {
    return azureConfigCache.get(normalized) ?? null
  }

  const pending = azureConfigRequests.get(normalized)
  if (pending) {
    return pending
  }

  const request = fetchAzureConfigFromApi(normalized)
    .then((config) => {
      azureConfigCache.set(normalized, config)
      azureConfigRequests.delete(normalized)
      return config
    })
    .catch((error) => {
      azureConfigRequests.delete(normalized)
      throw error
    })

  azureConfigRequests.set(normalized, request)
  return request
}

const getInteractionKey = (userType: AzureAuthUserType) => `${interactionKeyPrefix}.${userType.toLowerCase()}`

const getMsalInstance = async (userType?: string) => {
  const normalized = normalizeUserType(userType)
  const config = await getAzureConfig(normalized)
  if (!config) {
    return null
  }

  const key = `${config.clientId}|${config.authority}|${config.redirectUri}`
  const existing = msalInstances.get(key)
  if (existing) {
    return existing
  }

  const instance = new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: config.authority,
      redirectUri: config.redirectUri,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  })

  msalInstances.set(key, instance)
  return instance
}

export const isAzureConfigured = async (userType?: string) => {
  try {
    const config = await getAzureConfig(userType)
    return Boolean(config)
  } catch {
    return false
  }
}

const clearMsalInteraction = () => {
  if (typeof window === 'undefined') return
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.includes('interaction.status'))
      .forEach((key) => sessionStorage.removeItem(key))
  } catch {
    // ignore storage failures
  }
}

const getInteractionStartedAt = (userType: AzureAuthUserType) => {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(getInteractionKey(userType))
    if (!raw) return null
    const parsed = JSON.parse(raw) as { startedAt?: number }
    return typeof parsed?.startedAt === 'number' ? parsed.startedAt : null
  } catch {
    return null
  }
}

export const clearAzureInteraction = (userType?: string) => {
  if (typeof window === 'undefined') return
  try {
    if (userType) {
      sessionStorage.removeItem(getInteractionKey(normalizeUserType(userType)))
    } else {
      sessionStorage.removeItem(getInteractionKey('EMPLOYEE'))
      sessionStorage.removeItem(getInteractionKey('CONTRACTOR'))
    }
  } catch {
    // ignore storage failures
  }
  clearMsalInteraction()
}

const isInteractionInProgress = (userType: AzureAuthUserType) => {
  const startedAt = getInteractionStartedAt(userType)
  if (!startedAt) return false
  if (Date.now() - startedAt > interactionTtlMs) {
    clearAzureInteraction(userType)
    return false
  }
  return true
}

const markInteractionStarted = (userType: AzureAuthUserType) => {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(getInteractionKey(userType), JSON.stringify({ startedAt: Date.now() }))
  } catch {
    // ignore storage failures
  }
}

export const startAzureLogin = async (userType?: string) => {
  const normalizedUserType = normalizeUserType(userType)
  const instance = await getMsalInstance(normalizedUserType)
  const config = await getAzureConfig(normalizedUserType)

  if (!instance || !config) {
    throw new Error('Azure configuration is missing.')
  }
  if (isInteractionInProgress(normalizedUserType)) {
    throw new Error('Azure login is already in progress. Please wait a moment.')
  }
  clearMsalInteraction()
  markInteractionStarted(normalizedUserType)
  const request: RedirectRequest = {
    scopes: ['openid', 'profile', 'email'],
    redirectUri: config.redirectUri,
    prompt: normalizedUserType === 'CONTRACTOR' ? 'login' : undefined,
  }
  await instance.loginRedirect(request)
}

export const handleAzureRedirect = async (userType?: string): Promise<AuthenticationResult | null> => {
  const normalizedUserType = normalizeUserType(userType)
  const instance = await getMsalInstance(normalizedUserType)

  if (!instance) {
    return null
  }
  try {
    return await instance.handleRedirectPromise()
  } finally {
    clearAzureInteraction(normalizedUserType)
  }
}

export const logoutAzureSession = async (userType?: string, postLogoutPath = '/login') => {
  const normalizedUserType = normalizeUserType(userType)
  const instance = await getMsalInstance(normalizedUserType)

  clearAzureInteraction(normalizedUserType)

  if (!instance) {
    return false
  }

  const postLogoutRedirectUri =
    typeof window === 'undefined' ? postLogoutPath : `${window.location.origin}${postLogoutPath}`

  await instance.logoutRedirect({
    postLogoutRedirectUri,
  })

  return true
}
